import { useMsal } from "@azure/msal-react";
import { Fragment, useRef } from "react";
import { Dialog, Transition, TransitionChild, DialogPanel, DialogTitle } from "@headlessui/react";
import DOMPurify from "dompurify";
import { marked, MarkedOptions } from "marked";
import tickIcon from "../../assets/tick-icon.svg";

interface Props {
    open: boolean;
    setOpen: (open: boolean) => void;
}

export const Disclaimer = ({ open, setOpen }: Props) => {
    const cancelButtonRef = useRef(null);

    const { instance } = useMsal();
    const handleLogOutPopup = () => {
        instance
            .logoutPopup({
                mainWindowRedirectUri: "/",
                account: instance.getActiveAccount()
            })
            .catch(error => console.log(error));
    };

    const onClose = () => {
        setOpen(false);
    };

    const disclaimerText = `SIMBA is an artificial intelligence system, its responses are not individually approved, may contain errors, and are unique to the context of your query. 
Please remember:
- Language models can generate nonsensical content or fabricate content - <em>hallucinations</em> - that might sound reasonable but is inaccurate or outdated.
- Language models can potentially behave in ways that are unfair, unreliable, or offensive. These models can over or under-represent groups, erase representation of some groups, or reinforce demeaning or negative stereotypes. These models may produce other types of inappropriate or offensive content.
- SIMBA is not a substitute for thoroughly reading and understanding reports, review documents, handbooks, and published company policy documents.
- While we strive to ensure the accuracy and currency of the information provided by the assistant, errors or outdated information may occasionally occur.
- Responses generated by this AI tool may contain copyrighted material or inaccuracies. Users should independently verify any important or critical information.
- SIMBA must not replace a human in decision making. Complex scenarios or specific questions are best handled by consulting with the relevant member of staff.
- Conversations with the AI assistant may be recorded and monitored to improve service quality.

- By clicking “Accept”, you confirm that you have undertaken and passed the mandatory AI training.
- By clicking “Accept”, you confirm that you have read and understood the:`;

    const options: MarkedOptions = { async: false };
    const parsedDisclaimerText = marked.parse(disclaimerText, options) as string;

    const handleLinkClick = (link_url: string) => {
        window.open(link_url, "_blank");
    };

    return (
        <Transition show={open} as={Fragment}>
            <Dialog className="relative z-[100]" initialFocus={cancelButtonRef} onClose={() => undefined}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel className="relative transform overflow-hidden bg-sstl-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                                <div className="bg-sstl-white p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="ml-0 sm:mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-sstl-light-grey sm:mx-0 sm:h-10 sm:w-10">
                                            <img src={tickIcon} alt="icon-tick" />
                                        </div>
                                        <div className="mt-3 sm:ml-4 sm:mt-0 text-left">
                                            <DialogTitle as="h3" className="font-normal text-xl leading-6 text-black tracking-wider uppercase">
                                                Disclaimer
                                            </DialogTitle>
                                            <div className="chat-stream mt-2">
                                                <p
                                                    className="text-md tracking-wide custom-list custom-list-hyphen"
                                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(parsedDisclaimerText) }}
                                                ></p>
                                                <p className="text-md tracking-wide custom-list custom-list-hyphen pl-[32px]">
                                                    <ul>
                                                        <li>
                                                            <a
                                                                className="underline text-sstl-link hover:text-sstl-link-hover cursor-pointer"
                                                                onClick={() =>
                                                                    handleLinkClick(
                                                                        `https://surreysattech.sharepoint.com/:b:/r/sites/d-team-pdp/SSTLDocuments/Privacy%20Notice%20-%20SIMBA.pdf`
                                                                    )
                                                                }
                                                            >
                                                                Privacy Notice - SIMBA.pdf
                                                            </a>
                                                        </li>
                                                        <li>
                                                            <a
                                                                className="underline text-sstl-link hover:text-sstl-link-hover cursor-pointer"
                                                                onClick={() =>
                                                                    handleLinkClick(
                                                                        `https://surreysattech.sharepoint.com.mcas.ms/:b:/r/sites/d-team-mgmtsystm/DocSnapshots/SSTL%20Policy%20-%20AI%20Acceptable%20Use.pdf`
                                                                    )
                                                                }
                                                            >
                                                                SSTL Policy - AI Acceptable Use.pdf
                                                            </a>
                                                        </li>
                                                    </ul>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="px-4 pb-6 flex flex-row sm:flex-row-reverse sm:px-6 gap-4">
                                    <a className="sstl-primary-button px-4 py-2 m-0 w-[115px]" onClick={onClose}>
                                        <span className="tracking-wider">Accept</span>
                                    </a>
                                    <a className="sstl-secondary-button px-4 py-2 m-0 w-[115px]" onClick={handleLogOutPopup}>
                                        <span className="tracking-wider">Reject</span>
                                    </a>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
